import React from 'react';
import { LuMessageSquare, LuTrash2, LuX } from 'react-icons/lu';
import LoadingSpinner from '../common/LoadingSpinner';

// Chat titles can hold markdown from the first answer; the list shows plain text only.
const plainTitle = (chat) => {
  const title = chat?.title || chat?.lastMessage?.content || 'New conversation';
  return String(title).replace(/[#*_`>[\]()]/g, '').trim();
};

/**
 * The past Ask Me conversations, in a panel at the right of the Ask Me page
 * (beside the conversation on wide screens, over it on narrow ones).
 * It is closed by default: most questions are asked fresh, so history stays out of the way.
 */
const RecentChats = ({ chatList, selectedChatIndex, isDeleting, onSelectChat, onDeleteChat, onClose }) => {
  return (
    <aside
      aria-label="Recent questions"
      className="absolute inset-y-0 right-0 z-20 flex w-80 max-w-full shrink-0 flex-col border-l border-ui-line bg-ui-card shadow-pop lg:static lg:shadow-none"
    >
      <div className="flex h-12 items-center justify-between border-b border-ui-line-soft px-4">
        <h2 className="text-sm font-semibold text-ui-ink">Recent questions</h2>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close recent questions"
          className="-mr-1.5 flex size-8 items-center justify-center rounded-lg text-ui-ink-3 transition-colors hover:bg-ui-hover hover:text-ui-ink"
        >
          <LuX aria-hidden className="size-4" />
        </button>
      </div>

      {isDeleting && (
        <div className="flex items-center gap-2 border-b border-ui-line-soft px-4 py-2 text-xs text-ui-ink-2">
          <LoadingSpinner size="small" />
          Deleting…
        </div>
      )}

      <div className="ui-scroll flex-1 overflow-y-auto p-2">
        {chatList.length === 0 ? (
          <p className="px-3 py-8 text-center text-sm text-ui-ink-3">Questions you ask will appear here.</p>
        ) : (
          <ul className="flex flex-col gap-0.5">
            {chatList.map((chat, chatIndex) => {
              const isSelected = selectedChatIndex === chatIndex;
              return (
                <li key={chat?.sessionId || chatIndex} className="group relative">
                  <button
                    type="button"
                    onClick={() => onSelectChat(chatIndex)}
                    aria-current={isSelected ? 'true' : undefined}
                    className={`flex w-full items-start gap-2.5 rounded-lg py-2 pr-9 pl-3 text-left transition-colors ${
                      isSelected ? 'bg-ui-brand-soft' : 'hover:bg-ui-hover'
                    }`}
                  >
                    <LuMessageSquare aria-hidden className={`mt-0.5 size-4 shrink-0 ${isSelected ? 'text-brand-600 dark:text-brand-300' : 'text-ui-ink-3'}`} />
                    <span className="min-w-0">
                      <span className="line-clamp-2 text-sm text-ui-ink">{plainTitle(chat)}</span>
                      {chat?.messageCount !== undefined && (
                        <span className="mt-0.5 block text-xs text-ui-ink-3">
                          {chat.messageCount} {chat.messageCount === 1 ? 'message' : 'messages'}
                        </span>
                      )}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={(event) => onDeleteChat(event, chatIndex)}
                    aria-label="Delete this conversation"
                    title="Delete this conversation"
                    className="absolute top-2 right-1.5 flex size-7 items-center justify-center rounded-md text-ui-ink-3 opacity-0 transition-opacity group-hover:opacity-100 hover:bg-red-50 hover:text-red-600 focus-visible:opacity-100 dark:hover:bg-red-500/10 dark:hover:text-red-300"
                  >
                    <LuTrash2 aria-hidden className="size-3.5" />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </aside>
  );
};

export default RecentChats;
